// ====================================================
// VidyaTrack — Admin: Parents Management
// ====================================================

import React, { useState, useEffect } from "react";
import { FiSearch, FiPlus, FiEdit2, FiTrash2, FiEye, FiEyeOff } from "react-icons/fi";
import { useData } from "../../contexts/DataContext";
import { useAuth } from "../../contexts/AuthContext";
import Modal from "../../components/shared/Modal";
import { toast } from "react-toastify";

function generatePassword(name) {
  const first = (name || "Parent").split(" ")[0];
  return first.charAt(0).toUpperCase() + first.slice(1,4).toLowerCase() + "2025@";
}

export default function ParentsPage() {
  const { getParents, getStudents, updateUser, deleteUser: dbDeleteUser } = useData();
  const { register } = useAuth();

  const [parents,   setParents]   = useState([]);
  const [students,  setStudents]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editPr,    setEditPr]    = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [showPw,    setShowPw]    = useState(false);
  const [errors,    setErrors]    = useState({});

  const blank = { name:"", email:"", password:"", phone:"", childId:"", childName:"", childClass:"" };
  const [form, setForm] = useState(blank);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [pr, st] = await Promise.all([getParents(), getStudents()]);
      setParents(pr); setStudents(st);
    } catch { toast.error("Failed to load parents"); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditPr(null); setForm(blank); setErrors({}); setShowPw(false); setShowModal(true);
  };

  const openEdit = (p) => {
    setEditPr(p);
    setForm({ name:p.name||"", email:p.email||"", password:"", phone:p.phone||"", childId:p.children?.[0]||"", childName:p.childName||"", childClass:p.childClass||"" });
    setErrors({}); setShowPw(false); setShowModal(true);
  };

  const handleNameChange = (name) => {
    setForm(p => ({ ...p, name, password: editPr ? p.password : generatePassword(name) }));
    setErrors(p => ({...p, name:""}));
  };

  const handleChildChange = (studentId) => {
    const st = students.find(s => s.id === studentId);
    setForm(p => ({
      ...p,
      childId:   studentId,
      childName: st?.name || "",
      childClass:st?.className || "",
    }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim())  e.name  = "Full name is required";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = "Enter a valid email";
    if (!editPr) {
      if (!form.password)          e.password = "Password is required";
      else if (form.password.length < 6) e.password = "Minimum 6 characters";
    }
    return e;
  };

  const handleSave = async (ev) => {
    ev.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      if (editPr) {
        await updateUser(editPr.id, {
          name:      form.name.trim(),
          phone:     form.phone.trim(),
          children:  form.childId ? [form.childId] : [],
          childName: form.childName,
          childClass:form.childClass,
        });
        toast.success(`${form.name}'s profile updated!`);
      } else {
        await register({
          email:     form.email.trim().toLowerCase(),
          password:  form.password,
          name:      form.name.trim(),
          role:      "parent",
          phone:     form.phone.trim(),
          children:  form.childId ? [form.childId] : [],
          childName: form.childName,
          childClass:form.childClass,
        });
        toast.success(
          `✅ Parent account created!\nEmail: ${form.email}\nPassword: ${form.password}`,
          { autoClose: 8000 }
        );
      }
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(err.message);
      if (err.message?.includes("email")) setErrors(p => ({...p, email:err.message}));
    } finally { setSaving(false); }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    try { await dbDeleteUser(p.id); toast.success("Parent deleted!"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  const f = (key, val) => { setForm(p => ({...p,[key]:val})); setErrors(p => ({...p,[key]:""})); };

  const filtered = parents.filter(p =>
    (p.name||"").toLowerCase().includes(search.toLowerCase()) ||
    (p.email||"").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="animate-fade">
      <div className="page-head">
        <div>
          <h1 className="page-title">👨‍👩‍👧 Parents</h1>
          <p className="page-subtitle">{parents.length} parent{parents.length !== 1 ? "s" : ""} registered</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}><FiPlus size={15}/> Add Parent</button>
      </div>

      <div style={{ marginBottom:16 }}>
        <div className="search-box" style={{ maxWidth:380 }}>
          <FiSearch className="search-icon" size={14}/>
          <input placeholder="Search by name or email…" value={search} onChange={e=>setSearch(e.target.value)}/>
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div className="loading-center"><div className="spinner"/></div>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>#</th><th>Parent</th><th>Email</th><th>Phone</th><th>Child</th><th>Actions</th></tr></thead>
              <tbody>
                {filtered.map((p, i) => (
                  <tr key={p.id}>
                    <td style={{ color:"var(--gray-400)", fontWeight:600 }}>{i+1}</td>
                    <td>
                      <div style={{ display:"flex", alignItems:"center", gap:9 }}>
                        <div className="avatar av-sm" style={{ background:`hsl(${i*70},60%,88%)`, color:`hsl(${i*70},60%,30%)`, fontSize:".62rem" }}>
                          {(p.name||"?").split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase()}
                        </div>
                        <span style={{ fontWeight:600, fontSize:".88rem" }}>{p.name}</span>
                      </div>
                    </td>
                    <td style={{ fontSize:".82rem", color:"var(--gray-500)" }}>{p.email}</td>
                    <td style={{ fontSize:".82rem" }}>{p.phone || "—"}</td>
                    <td>
                      {p.childName
                        ? <div><div style={{ fontWeight:500, fontSize:".84rem" }}>{p.childName}</div><div style={{ fontSize:".72rem", color:"var(--gray-400)" }}>{p.childClass}</div></div>
                        : <span style={{ color:"var(--gray-400)", fontSize:".8rem" }}>Not linked</span>
                      }
                    </td>
                    <td>
                      <div style={{ display:"flex", gap:5 }}>
                        <button className="btn btn-secondary btn-sm btn-icon" onClick={() => openEdit(p)}><FiEdit2 size={13}/></button>
                        <button className="btn btn-danger btn-sm btn-icon" onClick={() => handleDelete(p)}><FiTrash2 size={13}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="empty-state">
                <div className="empty-icon">👨‍👩‍👧</div>
                <div className="empty-title">No parents yet</div>
                <div className="empty-desc">Click "Add Parent" to create a parent account.</div>
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        show={showModal}
        onClose={() => setShowModal(false)}
        title={editPr ? `Edit — ${editPr.name}` : "Create Parent Account"}
        footer={<>
          <button className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? <><div className="spinner spinner-sm"/> {editPr?"Saving…":"Creating…"}</> : editPr?"Save Changes":"Create Account"}
          </button>
        </>}
      >
        {!editPr && (
          <div style={{ background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:"var(--r8)", padding:"11px 14px", marginBottom:16, fontSize:".83rem", color:"#1d4ed8", display:"flex", gap:8 }}>
            <span>💡</span><div>Creates a real login account. Share credentials with the parent.</div>
          </div>
        )}

        <div className="g g2">
          <div className="form-group" style={{ gridColumn:"1/-1" }}>
            <label className="form-label">Full Name *</label>
            <input className={`form-control${errors.name?" error":""}`} placeholder="Parent's full name"
              value={form.name} onChange={e => handleNameChange(e.target.value)}/>
            {errors.name && <div className="form-error">{errors.name}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">Email *</label>
            <input type="email" className={`form-control${errors.email?" error":""}`} placeholder="parent@email.com"
              value={form.email}
              readOnly={!!editPr}
              style={editPr?{background:"var(--gray-100)",cursor:"not-allowed"}:{}}
              onChange={e => f("email", e.target.value)}/>
            {errors.email && <div className="form-error">{errors.email}</div>}
          </div>

          {!editPr && (
            <div className="form-group">
              <label className="form-label">Password *</label>
              <div className="input-wrap">
                <input type={showPw?"text":"password"} className={`form-control pr${errors.password?" error":""}`}
                  placeholder="Min. 6 characters" value={form.password} onChange={e => f("password", e.target.value)}/>
                <button type="button" className="icon-right" onClick={() => setShowPw(v=>!v)}
                  style={{ background:"none", border:"none", cursor:"pointer", display:"flex", alignItems:"center", color:"var(--gray-400)" }}>
                  {showPw ? <FiEyeOff size={14}/> : <FiEye size={14}/>}
                </button>
              </div>
              {errors.password && <div className="form-error">{errors.password}</div>}
              <div className="form-hint">Auto-generated: <strong>{form.password}</strong></div>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Phone</label>
            <input className="form-control" placeholder="+977-98XXXXXXXX" value={form.phone} onChange={e => f("phone", e.target.value)}/>
          </div>

          <div className="form-group" style={{ gridColumn:"1/-1" }}>
            <label className="form-label">Link to Student (Child)</label>
            <select className="form-control" value={form.childId} onChange={e => handleChildChange(e.target.value)}>
              <option value="">— Select child —</option>
              {students.map(s => (
                <option key={s.id} value={s.id}>{s.name}{s.rollNumber ? ` (${s.rollNumber})` : ""}{s.className ? ` — ${s.className}` : ""}</option>
              ))}
            </select>
            {students.length === 0 && <div className="form-hint">⚠ No students found. Add students first.</div>}
            {form.childName && <div className="form-hint">✅ Linked to: <strong>{form.childName}</strong> — {form.childClass}</div>}
          </div>
        </div>

        {!editPr && form.name && form.email && form.password && (
          <div style={{ marginTop:10, background:"var(--green-50)", border:"1px solid var(--green-100)", borderRadius:"var(--r8)", padding:"12px 14px" }}>
            <div style={{ fontSize:".78rem", fontWeight:700, color:"var(--green-600)", marginBottom:6 }}>📋 Credentials to share with parent:</div>
            <div style={{ fontSize:".82rem", fontFamily:"monospace", lineHeight:1.8, color:"var(--gray-700)" }}>
              <div>🌐 URL: <strong>vidyatrack3.web.app</strong></div>
              <div>📧 Email: <strong>{form.email}</strong></div>
              <div>🔑 Password: <strong>{form.password}</strong></div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
